import React from 'react';
import { Appointment, CompanyUser, Venue } from '@/types';
import { normalizeDateOnly } from '@/lib/dates';
import { type DeleteStep, type TeamMemberDraft } from '../types';
import {
  Edit2,
  AlertTriangle,
  UserPlus,
  ShieldCheck,
  CheckCircle,
  X,
  Loader2,
  Trash
} from 'lucide-react';

export interface CompanyDashboardModalsProps {
  editingBooking: Appointment | null;
  setEditingBooking: React.Dispatch<React.SetStateAction<Appointment | null>>;
  isLoadingEditingAvailability: boolean;
  editingTimeOptions: string[];
  bookingActionError: string;
  isSavingBooking: boolean;
  onCloseEditBooking: () => void;
  onSaveBooking: () => Promise<void>;
  onCancelBooking: () => Promise<void>;

  isAddingTeamMember: boolean;
  newMember: TeamMemberDraft;
  setNewMember: React.Dispatch<React.SetStateAction<TeamMemberDraft>>;
  memberError: string;
  isSavingMember: boolean;
  onCloseAddTeamMember: () => void;
  onAddTeamMember: () => Promise<void>;

  editingMember: CompanyUser | null;
  setEditingMember: React.Dispatch<React.SetStateAction<CompanyUser | null>>;
  teamEditError: string;
  isSavingTeamEdit: boolean;
  onCloseEditMember: () => void;
  onSaveTeamMember: () => Promise<void>;

  venues: Venue[];

  memberToDelete: CompanyUser | null;
  deleteStep: DeleteStep;
  onExecuteDelete: () => Promise<void>;
  onCloseDeleteMember: () => void;
}

const formatBookingDate = (appDate: string) => {
  const value = normalizeDateOnly(appDate);
  return value || '';
};

export const CompanyDashboardModals: React.FC<CompanyDashboardModalsProps> = ({
  editingBooking,
  setEditingBooking,
  isLoadingEditingAvailability,
  editingTimeOptions,
  bookingActionError,
  isSavingBooking,
  onCloseEditBooking,
  onSaveBooking,
  onCancelBooking,
  isAddingTeamMember,
  newMember,
  setNewMember,
  memberError,
  isSavingMember,
  onCloseAddTeamMember,
  onAddTeamMember,
  editingMember,
  setEditingMember,
  teamEditError,
  isSavingTeamEdit,
  onCloseEditMember,
  onSaveTeamMember,
  venues,
  memberToDelete,
  deleteStep,
  onExecuteDelete,
  onCloseDeleteMember
}) => {
  return (
    <>
      {editingBooking && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[80] p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-200 max-h-[90vh] flex flex-col">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <div>
                <h3 className="font-bold text-lg text-slate-900 uppercase tracking-tight">
                  Amend Appointment
                </h3>
                <p className="text-[10px] text-slate-400 font-black uppercase">
                  Ref: {editingBooking.id}
                </p>
              </div>
              <button
                onClick={onCloseEditBooking}
                className="text-slate-400 hover:text-slate-600 p-1.5"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              <div>
                <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                  Employee Name
                </label>
                <input
                  className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                  value={editingBooking.customerName}
                  onChange={(e) =>
                    setEditingBooking((prev) =>
                      prev ? { ...prev, customerName: e.target.value } : prev
                    )
                  }
                />
              </div>

              {editingBooking.status === 'booked' ? (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                        Date
                      </label>
                      <input
                        type="date"
                        className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                        value={formatBookingDate(editingBooking.date)}
                        onChange={(e) =>
                          setEditingBooking((prev) =>
                            prev ? { ...prev, date: e.target.value } : prev
                          )
                        }
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                        Time
                      </label>
                      <select
                        className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                        value={editingBooking.time}
                        onChange={(e) =>
                          setEditingBooking((prev) =>
                            prev ? { ...prev, time: e.target.value } : prev
                          )
                        }
                        disabled={isLoadingEditingAvailability}
                      >
                        {isLoadingEditingAvailability && (
                          <option value="">Loading availability...</option>
                        )}
                        {!isLoadingEditingAvailability && editingTimeOptions.length === 0 && (
                          <option value="">No available slots</option>
                        )}
                        {!isLoadingEditingAvailability &&
                          editingTimeOptions.map((timeOption) => (
                            <option key={timeOption} value={timeOption}>
                              {timeOption}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase mb-1">
                      Location
                    </label>
                    <select
                      className="w-full border-2 border-slate-100 p-2.5 rounded-xl bg-slate-50 outline-none text-sm"
                      value={editingBooking.venueId}
                      onChange={(e) =>
                        setEditingBooking((prev) =>
                          prev ? { ...prev, venueId: e.target.value } : prev
                        )
                      }
                    >
                      {venues.map((v) => (
                        <option key={v.id} value={v.id}>
                          {v.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <p className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                  Only booked appointments can be rescheduled.
                </p>
              )}

              {bookingActionError && (
                <p className="text-red-600 text-[10px] font-bold bg-red-50 p-2 rounded border border-red-100">
                  {bookingActionError}
                </p>
              )}
            </div>

            <div className="p-6 border-t bg-slate-50 flex justify-between gap-3">
              {editingBooking.status === 'booked' ? (
                <button
                  onClick={onCancelBooking}
                  disabled={isSavingBooking}
                  className="px-4 py-2 text-red-600 hover:bg-red-50 rounded font-bold disabled:opacity-50"
                >
                  Cancel Appointment
                </button>
              ) : (
                <span className="text-[10px] text-slate-400 font-bold uppercase tracking-widest">
                  Status: {editingBooking.status}
                </span>
              )}
              <div className="flex gap-2">
                <button
                  onClick={onCloseEditBooking}
                  className="px-4 py-2 text-slate-600 hover:bg-white rounded"
                  disabled={isSavingBooking}
                >
                  Close
                </button>
                <button
                  onClick={onSaveBooking}
                  disabled={isSavingBooking}
                  className="bg-purple-600 text-white px-6 py-2 rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2"
                >
                  {isSavingBooking ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isAddingTeamMember && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-purple-600" /> Add Team Member
              </h3>
              <button onClick={onCloseAddTeamMember}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-6">
              <div className="bg-amber-50 border border-amber-100 p-4 rounded-xl">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div className="text-sm text-amber-800">
                    <p className="font-bold">Important Security Warning</p>
                    <p className="mt-1">
                      You are granting this person <strong>full dashboard access</strong>. They will
                      be able to book medicals, view employee certificates, and manage company data.
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                    Full Name
                  </label>
                  <input
                    className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="Team Member Name"
                    value={newMember.name}
                    onChange={(e) => setNewMember((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                    Email Address
                  </label>
                  <input
                    type="email"
                    className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-purple-500"
                    placeholder="member@company.com"
                    value={newMember.email}
                    onChange={(e) => setNewMember((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </div>
              </div>

              {memberError && (
                <div className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded">
                  {memberError}
                </div>
              )}
            </div>
            <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
              <button onClick={onCloseAddTeamMember} className="px-6 py-2 text-slate-600 font-bold">
                Cancel
              </button>
              <button
                onClick={onAddTeamMember}
                disabled={isSavingMember}
                className="bg-purple-600 text-white px-8 py-2 rounded-xl font-bold hover:bg-purple-700 disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-purple-600/20"
              >
                {isSavingMember ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                Grant Access
              </button>
            </div>
          </div>
        </div>
      )}

      {editingMember && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-fade-in">
            <div className="p-6 border-b bg-slate-50 flex justify-between items-center">
              <h3 className="font-bold text-lg text-slate-800 flex items-center gap-2">
                <Edit2 className="w-5 h-5 text-teal-600" /> Edit Team Member
              </h3>
              <button onClick={onCloseEditMember}>
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                  Full Name
                </label>
                <input
                  className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Team Member Name"
                  value={editingMember.name}
                  onChange={(e) =>
                    setEditingMember((prev) => (prev ? { ...prev, name: e.target.value } : prev))
                  }
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase mb-1">
                  Email Address
                </label>
                <input
                  type="email"
                  className="w-full border rounded-xl p-3 bg-slate-50 outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="member@company.com"
                  value={editingMember.email}
                  onChange={(e) =>
                    setEditingMember((prev) => (prev ? { ...prev, email: e.target.value } : prev))
                  }
                />
                <p className="mt-1 text-[10px] text-slate-400">
                  Changing email updates the login email for this user.
                </p>
              </div>

              {teamEditError && (
                <div className="text-red-600 text-sm font-medium bg-red-50 p-2 rounded">
                  {teamEditError}
                </div>
              )}
            </div>
            <div className="p-6 border-t bg-slate-50 flex justify-end gap-3">
              <button onClick={onCloseEditMember} className="px-6 py-2 text-slate-600 font-bold">
                Cancel
              </button>
              <button
                onClick={onSaveTeamMember}
                disabled={isSavingTeamEdit}
                className="bg-teal-600 text-white px-8 py-2 rounded-xl font-bold hover:bg-teal-700 disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-teal-600/20"
              >
                {isSavingTeamEdit ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4" />
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {memberToDelete && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden animate-scale-up">
            <div className="p-8 text-center">
              {deleteStep === 'confirming' || deleteStep === 'idle' ? (
                <>
                  <div className="bg-red-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <Trash className="w-10 h-10 text-red-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    Revoke Dashboard Access?
                  </h3>
                  <p className="text-slate-500 text-sm mb-8 leading-relaxed">
                    Are you sure you want to remove <strong>{memberToDelete.name}</strong> from your
                    company dashboard? They will no longer be able to log in.
                  </p>
                  <div className="flex flex-col gap-3">
                    <button
                      onClick={onExecuteDelete}
                      className="w-full bg-red-600 text-white py-3 rounded-2xl font-bold hover:bg-red-700 transition-all shadow-lg shadow-red-600/20"
                    >
                      Yes, Delete Member
                    </button>
                    <button
                      onClick={onCloseDeleteMember}
                      className="w-full bg-slate-100 text-slate-600 py-3 rounded-2xl font-bold hover:bg-slate-200 transition-all"
                    >
                      No, Keep Access
                    </button>
                  </div>
                </>
              ) : deleteStep === 'processing' || deleteStep === 'verifying' ? (
                <div className="py-10">
                  <Loader2 className="w-16 h-16 text-red-600 animate-spin mx-auto mb-6" />
                  <h3 className="text-xl font-bold text-slate-900 mb-2">
                    {deleteStep === 'processing' ? 'Sending Request...' : 'Verifying Removal...'}
                  </h3>
                  <p className="text-slate-500 text-sm">Please wait while the system updates.</p>
                </div>
              ) : (
                <div className="py-10">
                  <div className="bg-green-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">User Deleted</h3>
                  <p className="text-slate-500 text-sm">
                    The member has been successfully removed.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};
